import { useI18n } from "../../i18n/useI18n";
import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { MythexMark } from "../../components/mythex-mark";
import { ModalSurface, useSurfaceClose } from "../../components/ui/Sheet";
import { api, googleAuthUrl } from "../../lib/api";
import { whenSignupFingerprint } from "../../lib/signup-fingerprint";
import { Spinner } from "../../components/ui";
import { useTurnstileToken } from "../../components/auth/Turnstile";
import { setPendingPrompt } from "../../lib/pending-prompt";
import { pendingReferralBonus } from "../../lib/referral";
import { fill } from "../../i18n";

const SURFACE_SHADOW =
  "rgba(119, 119, 113, 0.32) 0px 0px 0px 1px, oklch(0.272 0.002 107) 0px 0px 0px 1px, rgba(0, 0, 0, 0.16) 0px 0px 0px 2px, rgba(0, 0, 0, 0.08) 0px 2px 0px 0px, rgba(0, 0, 0, 0.08) 0px 2px 1px -0.5px, rgba(0, 0, 0, 0.08) 0px 4px 3px -1.5px, rgba(0, 0, 0, 0.08) 0px 7px 6px -3px, rgba(0, 0, 0, 0.08) 0px 13px 12px -6px, rgba(0, 0, 0, 0.08) 0px 25px 24px -12px";

const FIELD_SHADOW =
  "rgba(255, 255, 255, 0.08) 0px 1px 0px 0px inset, rgba(0, 0, 0, 0.08) 0px 2px 2px -1px inset, rgba(0, 0, 0, 0.08) 0px 4px 4px -2px inset, rgba(0, 0, 0, 0.12) 0px 1px 0px 0px, oklch(0.55 0.002 107 / 0.16) 0px 0px 0px 1px inset";

/** Dark button shadows + size (`h-8` / `rounded-2`). */
const SHADOW_BUTTON_NEUTRAL =
  "inset 0px 0.5px 0px 0px #ffffff29, inset 0px -0.5px 0px 0px #ffffff0a, inset 0px 0px 0px 0.5px #ffffff29, 0px 2px 2px -1px #0000001f, 0px 4px 4px -2px #0000001f";
const SHADOW_BUTTON_INVERSE =
  "inset 0px 0.5px 0px 0px #ffffff29, inset 0px -0.5px 0px 0px #ffffff0a, inset 0px 0px 0px 0.5px #fff, 0px 2px 2px -1px #0000001f, 0px 4px 4px -2px #0000001f";

const BTN =
  "inline-flex h-8 min-h-8 w-full items-center justify-center gap-2 rounded-[8px] px-2.5 text-sm font-normal transition";

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden className="size-4 shrink-0">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function OutlineButton({ children, href }: { children: React.ReactNode; href?: string }) {
  return (
    <a
      href={href}
      className={`${BTN} text-white hover:bg-white/[0.04]`}
      style={{ boxShadow: SHADOW_BUTTON_NEUTRAL }}
    >
      {children}
    </a>
  );
}

/**
 * Marketing overlay: Google + optional email preview.
 * Continue goes to the full page for that email: sign-in when it has an
 * account (or its company signs in with SSO), sign-up when it doesn't —
 * whichever of Log in / Get started opened the modal. No email: that page.
 */
export function AuthModal({
  onClose,
  prompt,
  mode = "signup",
  onSwitchMode,
}: {
  onClose: () => void;
  prompt?: string;
  mode?: "login" | "signup";
  onSwitchMode?: (mode: "login" | "signup") => void;
}) {
  const { m } = useI18n();
  const navigate = useNavigate();
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  // The lookup says whether an account exists, so it asks for a person (routes/sso.ts).
  const human = useTurnstileToken();
  const isSignup = mode === "signup";
  const [inviteBonus] = useState(() => pendingReferralBonus());

  useEffect(() => {
    if (prompt?.trim()) setPendingPrompt(prompt);
  }, [prompt]);

  // Worked out on this page only, for the per-browser signup limit (lib/signup-fingerprint.ts).
  const [fingerprint, setFingerprint] = useState<string | undefined>();
  useEffect(() => {
    let live = true;
    void whenSignupFingerprint().then((fp) => live && setFingerprint(fp));
    return () => {
      live = false;
    };
  }, []);

  const goToAuthPage = async (e?: FormEvent) => {
    e?.preventDefault();
    if (checking) return;
    const params = new URLSearchParams();
    const trimmed = email.trim();
    let signup = isSignup;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setChecking(true);
      try {
        const token = await human.take();
        const q = new URLSearchParams({ email: trimmed.toLowerCase(), ...(token ? { turnstileToken: token } : {}) });
        const r = await api.get<{ exists: boolean; sso: boolean }>(`/v1/auth/email-lookup?${q.toString()}`);
        signup = !r.exists && !r.sso;
      } catch {
        // Couldn't tell (offline, rate limited): the page the modal was opened for.
      } finally {
        setChecking(false);
      }
    }
    if (trimmed) params.set("email", trimmed.toLowerCase());
    if (prompt?.trim()) {
      setPendingPrompt(prompt);
      params.set("prompt", prompt.trim());
    }
    const qs = params.toString();
    onClose();
    navigate(`${signup ? "/signup" : "/login"}${qs ? `?${qs}` : ""}`);
  };

  return (
    <ModalSurface
      onClose={onClose}
      label={isSignup ? m.auth.createAccount : m.auth.welcomeBack}
      testId="auth-modal"
      className="relative max-w-[24rem] overflow-hidden rounded-[24px] bg-[#272727] p-6 text-white"
      style={{ boxShadow: SURFACE_SHADOW }}
    >
      <AuthModalClose />

      <div className="flex w-full min-w-0 flex-col gap-4">
        <MythexMark size={36} className="shrink-0" />
        <div className="pe-10">
          <p className="text-2xl font-medium tracking-tight text-muted opacity-50">
            {m.home.heroTitle}
          </p>
          <h2 className="text-2xl leading-none font-medium text-white">
            {isSignup ? m.auth.createAccount : m.auth.welcomeBack}
          </h2>
        </div>
        {isSignup && inviteBonus > 0 && (
          <p className="rounded-[12px] bg-white/[0.06] px-3 py-2 text-[13px] leading-snug text-white/85">
            {fill(m.referral.invited, { n: inviteBonus })}
          </p>
        )}

        <div className="flex w-full min-w-0 flex-col gap-2">
          <OutlineButton href={googleAuthUrl(undefined, undefined, fingerprint)}>
            <GoogleIcon />
            {m.auth.continueGoogle}
          </OutlineButton>
        </div>

        <div className="relative w-full">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#272727] px-2 text-muted">{m.auth.or}</span>
          </div>
        </div>

        <form onSubmit={(e) => void goToAuthPage(e)} className="flex w-full min-w-0 flex-col gap-3" noValidate autoComplete="off">
          <div>
            <label htmlFor={emailId} className="sr-only">
              {m.auth.email}
            </label>
            <input
              id={emailId}
              type="email"
              autoFocus
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={m.auth.email}
              className="h-8 w-full rounded-[8px] bg-transparent px-3 text-sm text-white outline-none placeholder:text-muted"
              style={{ boxShadow: FIELD_SHADOW }}
            />
          </div>
          {human.element}
          <button
            type="submit"
            disabled={checking}
            className={`${BTN} bg-white text-zinc-900 hover:bg-white/95 disabled:opacity-80`}
            style={{ boxShadow: SHADOW_BUTTON_INVERSE }}
            data-testid="auth-modal-continue"
          >
            {checking ? <Spinner size={14} /> : m.auth.continue}
          </button>
        </form>

        <p className="w-full text-center text-sm text-muted">
          {isSignup ? (
            <>
              {m.auth.haveAccount}{" "}
              <button
                type="button"
                className="text-white underline hover:text-white"
                onClick={() => onSwitchMode?.("login")}
              >
                {m.auth.logIn}
              </button>
            </>
          ) : (
            <>
              {m.auth.noAccount}{" "}
              <button
                type="button"
                className="text-white underline hover:text-white"
                onClick={() => onSwitchMode?.("signup")}
              >
                {m.auth.createAccount}
              </button>
            </>
          )}
        </p>

        <div className="w-full border-t border-white/10" />

        <p className="w-full text-center text-[11px] leading-relaxed text-pretty text-muted">
          {m.auth.agreePrefix}{" "}
          <Link to="/terms" className="underline hover:text-white" onClick={onClose}>
            {m.auth.termsOfService}
          </Link>{" "}
          {m.auth.and}{" "}
          <Link to="/privacy" className="underline hover:text-white" onClick={onClose}>
            {m.auth.privacyPolicy}
          </Link>
          .
        </p>
      </div>
    </ModalSurface>
  );
}

/** The ✕: through the surface, so the modal animates out. */
function AuthModalClose() {
  const { m } = useI18n();
  const close = useSurfaceClose();
  return (
    <button
      type="button"
      onClick={close}
      className="absolute top-4 end-4 z-20 grid min-h-11 min-w-11 place-items-center rounded-[8px] text-white/70 transition hover:bg-white/[0.06] hover:text-white"
      aria-label={m.auth.dismiss}
      data-testid="auth-modal-close"
    >
      <X className="h-4 w-4" strokeWidth={1.75} />
    </button>
  );
}
